// Legacy analytics shim — Sprint 2 (2026-05-21).
//
// Historical entry point (`trackEvent` / `trackPurchase` / `trackPageView`) is
// now a THIN BRIDGE onto the typed observability pipeline in
// `src/lib/observability/`. Call sites retain their original signatures so
// migration is incremental and rollback-safe.
//
// What still happens here:
//  - Maps legacy event names → typed `analytics.track(...)` calls.
//  - `trackPurchase` still fires the Google Ads conversion via `gtag()` for
//    the AW-… measurement (GA4Provider configures the Ads tag after consent).
//  - `trackPageView` is now a no-op: page tracking is driven by
//    `AnalyticsProvider` → `useTrackPageView` → `analytics.page()`.
//
// Anything not mapped is forwarded as a `cta_clicked` shim so legacy events
// remain visible in dev without polluting the typed schema.

import { analytics } from "@/lib/observability";
import type { EventName } from "@/lib/observability";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

// Google Ads conversion label for "Compra".
const GOOGLE_ADS_PURCHASE_SEND_TO = "AW-304161929/FaPACJvR96scEInJhJEB";

// Kept for backwards-compat. No longer used (GTM removed).
export const GTM_ID: string | undefined = undefined;

type LegacyParams = Record<string, unknown>;

function num(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback = ""): string {
  return v == null ? fallback : String(v);
}

/**
 * Bridge legacy event names to typed pipeline events.
 * Unknown names → `cta_clicked` with the original event as the cta_id so
 * nothing is silently dropped.
 */
function bridgeLegacy(event: string, params: LegacyParams): void {
  switch (event) {
    case "login":
      analytics.track("auth_login_completed", { method: str(params.method, "password") });
      return;
    case "sign_up":
      analytics.track("auth_signup_completed", { method: str(params.method, "password") });
      return;
    case "wallet_deposit_request":
      analytics.track("wallet_topup_started", {
        method: (params.method === "pix" ? "pix" : "stripe"),
        value: num(params.value ?? params.amount),
        currency: "BRL",
      });
      return;
    case "wallet_deposit":
      analytics.track("wallet_topup_completed", {
        method: (params.method === "pix" ? "pix" : "stripe"),
        transaction_id: str(params.transaction_id ?? params.session_id ?? Date.now()),
        value: num(params.value ?? params.amount),
        currency: "BRL",
      });
      return;
    case "begin_checkout":
      analytics.track("checkout_started", {
        kind: "product",
        value: num(params.value),
        currency: str(params.currency, "BRL") as "BRL" | "USD" | "EUR",
      });
      return;
    case "mass_order_submit": {
      const okCount = num(params.success);
      if (okCount > 0) {
        analytics.track("order_created", {
          order_id: `mass-${str(params.service_id)}-${Date.now()}`,
          value: num(params.value),
          currency: str(params.currency, "BRL") as "BRL" | "USD" | "EUR",
          item_count: okCount,
        });
      }
      return;
    }
    case "view_item":
      analytics.track("catalog_product_viewed", {
        product_id: str(params.item_id ?? params.product_id ?? "unknown"),
        category: str(params.item_category, "") || undefined,
      });
      return;
    case "search":
      analytics.track("catalog_search_performed", {
        query_hash: str(params.search_term ?? params.query, ""),
        results_count: num(params.results_count),
      });
      return;
    default:
      // Forward unrecognized legacy events as a generic CTA so they remain
      // visible during the migration window without expanding the schema.
      analytics.track("cta_clicked", {
        cta_id: event,
        location: str(params.location, "legacy_bridge"),
      });
  }
}

export function trackEvent(event: string, params: LegacyParams = {}): void {
  try {
    // Maintain legacy dataLayer push for any residual GTM/CMP listeners.
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, event_name: event, ...params });
    }
    bridgeLegacy(event, params);
  } catch {
    /* never throw from analytics */
  }
}

export function trackEventOnce(storageKey: string, event: string, params: LegacyParams = {}): void {
  try {
    if (typeof window !== "undefined" && window.sessionStorage.getItem(storageKey)) return;
    if (typeof window !== "undefined") window.sessionStorage.setItem(storageKey, "1");
  } catch {
    /* ignore storage errors and still track */
  }
  trackEvent(event, params);
}

/**
 * No-op since Sprint 2 — `AnalyticsProvider` drives all pageviews via
 * `analytics.page()`. Kept as an exported symbol for backwards compatibility
 * with imports that haven't been refactored yet.
 */
export function trackPageView(_path: string): void {
  /* intentionally empty — see file header */
}

export function trackPurchase(params: {
  transaction_id: string;
  value: number;
  currency?: string;
  items?: unknown[];
}): void {
  const currency = (params.currency || "BRL") as "BRL" | "USD" | "EUR";
  try {
    analytics.track("checkout_completed", {
      kind: "product",
      transaction_id: String(params.transaction_id || Date.now()),
      value: num(params.value),
      currency,
      item_count: Array.isArray(params.items) ? params.items.length : undefined,
    });
  } catch {
    /* swallow */
  }
  // Fire Google Ads conversion. GA4Provider configures the AW tag after
  // consent → before that, the early gtag stub buffers into dataLayer.
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: GOOGLE_ADS_PURCHASE_SEND_TO,
        value: params.value,
        currency,
        transaction_id: String(params.transaction_id || ""),
      });
    }
  } catch {
    /* never throw from analytics */
  }
}

// Re-export the typed pipeline so call sites can incrementally migrate
// without changing import paths.
export { analytics } from "@/lib/observability";
export type { EventName } from "@/lib/observability";
